import React, {useEffect, useState} from 'react';
import dayjs from 'dayjs'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Calendar, dayjsLocalizer} from 'react-big-calendar'
import {useAuth} from "../providers/AuthProvider";
import {useCalendar, useProfile, useStats, useUserData} from "../providers/UserDataProvider";
import styled from "@emotion/styled";
import {Link, useLocation} from "react-router-dom";
import {useTheme} from "@mui/system";
import RenderSocialIcons from "../components/RenderSocialIcons";
import {useFeatureAccess} from "../hooks/useFeatureAccess";
import TemplateCard from "../components/TemplateCard";
import ContentBucketCard from "../components/ContentBucketCard";
import ContentBucketList from "../components/ContentBucketList";
import EmptyBucketsState from "../components/EmptyBucketsState";
import ContentBucketForm from "../components/ContentBucketForm";
import IndustryTemplateSelector from "../components/IndustryTemplateSelector";
import {ContentBucketsProvider, useContentBuckets} from "../providers/ContentBucketsProvider";
import ScheduleView from "../components/ScheduleView";
import HomeWeekView from "../components/schedule/HomeWeekView";
import StatCard from "../components/StatCard";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Fab,
    Fade,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    Collapse,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Facebook as FacebookIcon,
    FilterList as FilterIcon,
    Image as ImageIcon,
    Instagram as InstagramIcon,
    Link as LinkIcon,
    LinkedIn as LinkedInIcon,
    PostAdd as PostIcon,
    Search as SearchIcon,
    Twitter as TwitterIcon,
    VideoLibrary as VideoIcon,
    ViewList as ListIcon,
    ViewModule as GridIcon,
    YouTube as YouTubeIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import WebStoriesIcon from '@mui/icons-material/WebStories';
import CategoryIcon from '@mui/icons-material/Category';
import TikTokLogo from '../components/TikTokLogo';
import BlueskyLogo from '../components/BlueskyLogo';
import ThreadsLogo from '../components/ThreadsLogo';
import MastodonLogo from '../components/MastodonLogo';

const localizer = dayjsLocalizer(dayjs);

const DashboardContainer = styled(Box)(({theme}) => ({
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    paddingBottom: theme.spacing(4),
    width: '100%',
    maxWidth: '100vw',
    overflow: 'hidden',
    boxSizing: 'border-box',
}));

const FeatureCard = styled(Card)(({theme}) => ({
    height: '100%', border: `1px solid ${theme.palette.grey[300]}`, transition: 'all 0.3s ease', '&:hover': {
        boxShadow: theme.shadows[4],
    },
}));

const ProFeatureCard = styled(Card)(({theme}) => ({
    height: '100%',
    border: `2px solid ${theme.palette.secondary.main}`,
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.common.white,
    position: 'relative',
    overflow: 'visible',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -8,
        right: -8,
        width: 40,
        height: 40,
        background: theme.palette.warning.main,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const SchedulerCard = styled(Card)(({theme}) => ({
    padding: theme.spacing(3), marginBottom: theme.spacing(3), border: `1px solid ${theme.palette.grey[300]}`,
}));

const TimeSlot = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    minHeight: 40,
    fontSize: '0.875rem',
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
    },
}));

const WeeklyCalendar = styled(Box)(({theme}) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    '& .header': {
        fontWeight: 600,
        textAlign: 'center',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(1),
    },
    '& .time-label': {
        fontSize: '0.75rem', color: theme.palette.text.secondary, textAlign: 'center', padding: theme.spacing(1),
    },
}));

const UpgradeWrapper = styled(Paper)(({theme}) => ({
    margin: theme.spacing(2),
    padding: theme.spacing(5),
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    borderRadius: theme.spacing(2),
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(2),
}));

const MessageWrapper = styled(Paper)(({theme}) => ({
    margin: theme.spacing(2),
    padding: theme.spacing(5),
    backgroundColor: theme.palette.grey[80],
    color: theme.palette.grey[100],
    display: 'flex',
    borderRadius: theme.spacing(2),
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
}));

const StyledCalendar = styled(Calendar)(({theme}) => ({
    '& .rbc-event': {
        backgroundColor: 'transparent', border: 'none',
    }, '& .rbc-event-content': {
        height: '100%',
    }, '& .rbc-row-segment': {
        padding: theme.spacing(0.25),
    },
}));

const CustomEventCard = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.spacing(2),
    backgroundColor: 'transparent',
    width: '100%',
}));

const ContentBucketContainer = styled(Box)(({theme}) => ({
    padding: theme.spacing(2),
}));

const PageHeader = styled(Box)(({theme}) => ({
    marginBottom: theme.spacing(4), [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2)
    }
}));

const ControlsContainer = styled(Box)(({theme}) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column', alignItems: 'stretch', '& > *': {
            width: '100%'
        }
    }
}));

const SearchContainer = styled(Box)(({theme}) => ({
    flexGrow: 1, maxWidth: 400, [theme.breakpoints.down('sm')]: {
        maxWidth: 'none'
    }
}));

const ViewToggleContainer = styled(Box)(({theme}) => ({
    display: 'flex', gap: theme.spacing(1), [theme.breakpoints.down('sm')]: {
        justifyContent: 'center'
    }
}));

const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const timeSlots = ['12:30 AM', '1:30 AM', '2:30 AM', '3:30 AM', '4:30 AM', '5:30 AM'];

// New styled component for social account display
const SocialAccountCard = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
    },
}));

const PlatformGroup = styled(Box)(({theme}) => ({
    marginBottom: theme.spacing(2), '&:last-child': {
        marginBottom: 0,
    },
}));

/**
 * Content Buckets Tab Component
 * Full implementation of content bucket management with all features from ContentBucketsPage
 */
const ContentBucketsTab = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        buckets, loading, error, fetchBuckets, clearError, bucketCount, totalPosts
    } = useContentBuckets();

    // Local state - matches ContentBucketsPage functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTemplate, setFilterTemplate] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);

    // Load buckets on component mount
    useEffect(() => {
        fetchBuckets();
    }, [fetchBuckets]);

    // Apply filters
    const filteredBuckets = buckets.filter(bucket => {
        const matchesSearch = !searchTerm || bucket.name.toLowerCase().includes(searchTerm.toLowerCase()) || bucket.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesTemplate = !filterTemplate || bucket.industry_template === filterTemplate;

        const matchesActive = filterActive === 'all' || (filterActive === 'active' && bucket.is_active) || (filterActive === 'inactive' && !bucket.is_active);

        return matchesSearch && matchesTemplate && matchesActive;
    });

    // Handle search
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle create new bucket
    const handleCreateNew = () => {
        setShowCreateForm(true);
    };

    // Handle create from template
    const handleCreateFromTemplate = () => {
        setShowTemplateSelector(true);
    };

    // Handle close forms
    const handleCloseCreateForm = () => {
        setShowCreateForm(false);
    };

    const handleCloseTemplateSelector = () => {
        setShowTemplateSelector(false);
    };

    // Handle form success
    const handleCreateSuccess = () => {
        setShowCreateForm(false);
    };

    // Get unique templates for filter
    const availableTemplates = [...new Set(buckets.map(bucket => bucket.industry_template))]
        .filter(Boolean);

    if (loading && buckets.length === 0) {
        return (<ContentBucketContainer>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={48}/>
            </Box>
        </ContentBucketContainer>);
    }

    return (<ContentBucketContainer>
        {/* Page Header */}
        <PageHeader>
            <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{
                    fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}, fontWeight: 700
                }}
            >
                Content Buckets
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{fontSize: {xs: '0.9rem', sm: '1rem'}}}
            >
                Organize your content into thematic categories for better planning and variety.
                {bucketCount > 0 && (<> You
                    have {bucketCount} bucket{bucketCount !== 1 ? 's' : ''} with {totalPosts} total posts.</>)}
            </Typography>
        </PageHeader>

        {/* Error Alert */}
        {error && (<Fade in={!!error}>
            <Alert
                severity="error"
                onClose={clearError}
                sx={{mb: 3}}
            >
                {error}
            </Alert>
        </Fade>)}

        {/* Empty State */}
        {buckets.length === 0 && !loading && (<EmptyBucketsState
            onCreateNew={handleCreateNew}
            onCreateFromTemplate={handleCreateFromTemplate}
        />)}

        {/* Controls and Content */}
        {buckets.length > 0 && (<>
            {/* Search and Filter Controls */}
            <ControlsContainer>
                {/* Search */}
                <SearchContainer>
                    <TextField
                        fullWidth
                        placeholder="Search buckets..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size={isMobile ? "medium" : "small"}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start">
                                <SearchIcon color="action"/>
                            </InputAdornment>),
                        }}
                    />
                </SearchContainer>

                {/* Template Filter */}
                <FormControl size={isMobile ? "medium" : "small"} sx={{minWidth: 140}}>
                    <InputLabel>Template</InputLabel>
                    <Select
                        value={filterTemplate}
                        onChange={(e) => setFilterTemplate(e.target.value)}
                        label="Template"
                    >
                        <MenuItem value="">All Templates</MenuItem>
                        {availableTemplates.map(template => (<MenuItem key={template} value={template}>
                            {template.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </MenuItem>))}
                    </Select>
                </FormControl>

                {/* Active Filter */}
                <FormControl size={isMobile ? "medium" : "small"} sx={{minWidth: 120}}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        label="Status"
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>

                {/* View Toggle */}
                {!isMobile && (<ViewToggleContainer>
                    <Button
                        variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                        onClick={() => setViewMode('grid')}
                        startIcon={<GridIcon/>}
                        size="small"
                    >
                        Grid
                    </Button>
                    <Button
                        variant={viewMode === 'list' ? 'contained' : 'outlined'}
                        onClick={() => setViewMode('list')}
                        startIcon={<ListIcon/>}
                        size="small"
                    >
                        List
                    </Button>
                </ViewToggleContainer>)}

                {/* Create Buttons */}
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    <Button
                        variant="outlined"
                        onClick={handleCreateFromTemplate}
                        size={isMobile ? "medium" : "small"}
                        startIcon={<FilterIcon/>}
                    >
                        From Template
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateNew}
                        size={isMobile ? "medium" : "small"}
                        startIcon={<AddIcon/>}
                    >
                        New Bucket
                    </Button>
                </Box>
            </ControlsContainer>

            {/* Buckets Display */}
            {filteredBuckets.length === 0 ? (<Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="300px"
                textAlign="center"
            >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No buckets match your filters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Try adjusting your search or filter criteria
                </Typography>
            </Box>) : (<>
                {viewMode === 'grid' ? (<Grid container spacing={3}>
                    {filteredBuckets.map(bucket => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={bucket.id}>
                            <ContentBucketCard bucket={bucket}/>
                        </Grid>))}
                </Grid>) : (<ContentBucketList buckets={filteredBuckets}/>)}
            </>)}
        </>)}

        {/* Floating Action Button for Mobile */}
        {isMobile && buckets.length > 0 && (<Fab
            color="primary"
            aria-label="add bucket"
            onClick={handleCreateNew}
            sx={{
                position: 'fixed', bottom: 24, right: 24,
            }}
        >
            <AddIcon/>
        </Fab>)}

        {/* Create Form Dialog */}
        <ContentBucketForm
            open={showCreateForm}
            onClose={handleCloseCreateForm}
            onSuccess={handleCreateSuccess}
        />

        {/* Template Selector Dialog */}
        <IndustryTemplateSelector
            open={showTemplateSelector}
            onClose={handleCloseTemplateSelector}
            onSuccess={handleCreateSuccess}
        />
    </ContentBucketContainer>);
};

/**
 * Main Dashboard component that provides different views based on URL parameters
 * and feature access permissions. Includes scheduling interface and calendar management.
 *
 * @component
 * @example
 * return (
 *   <DashboardPage />
 * )
 */
function DashboardPage() {
    const auth = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();

    // Use the new optimized user data hooks
    const userData = useUserData();
    const stats = useStats();
    const calendar = useCalendar();
    const profile = useProfile();

    const {
        canAccessTemplateEditor, canCreatePosts, canCreateMedia, canAccessSocialConnections
    } = useFeatureAccess();

    // Get current view from URL parameters or location state
    const getActiveView = () => {
        if (location.state?.activeTab) {
            return location.state.activeTab;
        }
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('tab') || 'home';
    };

    // State management
    const [activeView, setActiveView] = useState(getActiveView);
    const [templates, setTemplates] = useState([]);
    const [post, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [autoSchedulerEnabled, setAutoSchedulerEnabled] = useState(false);
    const [manualSchedulerEnabled, setManualSchedulerEnabled] = useState(true);
    const [schedulerSettings, setSchedulerSettings] = useState({
        startTime: '12:30 AM', endTime: '04:00 AM', interval: '12:30', selectedDays: [], selectedTimes: []
    });

    // Template filtering state
    const [templateSearchTerm, setTemplateSearchTerm] = useState('');
    const [templateTypeFilter, setTemplateTypeFilter] = useState('all'); // 'all', 'video', 'image'
    const [templateViewMode, setTemplateViewMode] = useState('grid');
    
    // Scheduler config state
    const [expandedPlatforms, setExpandedPlatforms] = useState(new Set());

    // Update active view when location changes
    useEffect(() => {
        setActiveView(getActiveView());
    }, [location]);

    // Extract user data using optimized providers with fallback to legacy auth data
    const user_details = profile.data || (auth.user?.[0] || {});
    // Note: posts_cal_events is now handled directly by ScheduleView using the optimized calendar API
    const is_social_accounts_set = (profile.data?.social_accounts?.length > 0) || (auth.user?.[0]?.userprofile?.social_accounts?.length > 0 || false);

    const isAccountActive = (profile.data?.subscription_status === 'active') || (profile.data?.subscription_status === 'trialing') || (profile.data?.subscription_status === 'trial_ending_soon') || user_details?.userprofile?.subscription_status === 'active' || user_details?.userprofile?.subscription_status === 'trialing' || user_details?.userprofile?.subscription_status === 'trial_ending_soon';

    const isAccountExpired = (profile.data?.subscription_status === 'expired') || user_details?.userprofile?.subscription_status === 'expired';

    // Legacy post transformation (now deprecated - ScheduleView uses optimized calendar API)
    const newData = post?.map(event => ({
        ...event,
        'title': event.platform_details[0]?.platform_specific_text || event.post_text,
        id: event.id,
        start: new Date(event.created_at),
        end: new Date(event.platform_details[0]?.posted_at || event.created_at),
        is_posted: event.platform_details.every(detail => detail.is_posted)
    }));

    // Effects for data fetching
    useEffect(() => {
        if (canAccessTemplateEditor) {
            fetchTemplates();
        }
    }, [canAccessTemplateEditor]);

    useEffect(() => {
        fetchPosts();
    }, []);

    /**
     * Helper function to get platform icon - Updated with proper logo components
     */
    const getPlatformIcon = (platform, size = 'small') => {
        const iconProps = {fontSize: size, color: 'primary'};

        switch (platform?.toLowerCase()) {
            case 'twitter':
                return <TwitterIcon {...iconProps} />;
            case 'facebook':
                return <FacebookIcon {...iconProps} />;
            case 'instagram':
                return <InstagramIcon {...iconProps} />;
            case 'linkedin':
            case 'linkedin_org':
                return <LinkedInIcon {...iconProps} />;
            case 'youtube':
                return <YouTubeIcon {...iconProps} />;
            case 'tiktok':
                return <TikTokLogo sx={{ fontSize: size === 'small' ? 20 : 24, color: 'primary.main' }} />;
            case 'bluesky':
                return <BlueskyLogo sx={{ fontSize: size === 'small' ? 20 : 24, color: 'primary.main' }} />;
            case 'threads':
                return <ThreadsLogo sx={{ fontSize: size === 'small' ? 20 : 24, color: 'primary.main' }} />;
            case 'mastodon':
                return <MastodonLogo sx={{ fontSize: size === 'small' ? 20 : 24, color: 'primary.main' }} />;
            default:
                return <LinkIcon {...iconProps} />;
        }
    };

    /**
     * Helper function to get platform display name
     */
    const getPlatformDisplayName = (platform) => {
        const platformNames = {
            'twitter': 'Twitter',
            'facebook': 'Facebook',
            'instagram': 'Instagram',
            'linkedin': 'LinkedIn',
            'linkedin_org': 'LinkedIn Organization',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'bluesky': 'Bluesky',
            'threads': 'Threads',
            'mastodon': 'Mastodon'
        };

        return platformNames[platform?.toLowerCase()] || platform;
    };

    /**
     * Helper function to group accounts by platform
     */
    const groupAccountsByPlatform = (accounts) => {
        return accounts.reduce((groups, account) => {
            const platform = account.platform.toLowerCase();
            if (!groups[platform]) {
                groups[platform] = [];
            }
            groups[platform].push(account);
            return groups;
        }, {});
    };

    /**
     * Toggle platform expansion in scheduler config
     */
    const togglePlatform = (platform) => {
        const newExpanded = new Set(expandedPlatforms);
        if (newExpanded.has(platform)) {
            newExpanded.delete(platform);
        } else {
            newExpanded.add(platform);
        }
        setExpandedPlatforms(newExpanded);
    };

    /**
     * Fetches user templates from the API
     */
    const fetchTemplates = async () => {
        try {
            const url = process.env.REACT_APP_API_BASE_URL + 'templates/';
            const response = await fetch(url, {
                method: 'GET', headers: {
                    'Content-Type': 'application/json', 'Authorization': `Token ${localStorage.getItem('token')}`
                },
            });
            const data = await response.json();
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    /**
     * Fetches user posts, optionally filtered by target date
     * @param {Date} targetDate - Optional date to filter posts
     */
    const fetchPosts = async (targetDate) => {
        try {
            setPostsLoading(true);
            const url = new URL(`${process.env.REACT_APP_API_BASE_URL}post/posts/`);

            if (targetDate) {
                url.searchParams.append('target_date', targetDate.toISOString().split('T')[0]);
            } else {
                const today = new Date();
                url.searchParams.append('target_date', today.toISOString().split('T')[0]);
            }

            const response = await fetch(url.toString(), {
                method: 'GET', headers: {
                    'Content-Type': 'application/json', 'Authorization': `Token ${localStorage.getItem('token')}`
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch posts');
            }

            const data = await response.json();
            setPosts(data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setPostsLoading(false);
        }
    };

    /**
     * Handles navigation to a specific view
     * @param {string} view - The view to navigate to
     */
    const handleViewChange = (view) => {
        setActiveView(view);
    };

    /**
     * Navigate to the calendar view
     */
    const handleNavigateToCalendar = () => {
        setActiveView('calendar');
    };

    /**
     * Handles calendar navigation
     * @param {Date} date - The target date
     */
    const handleNavigate = (date) => {
        fetchPosts(date);
    };

    /**
     * Handles post event click in calendar
     * @param {Object} event - The clicked event
     */
    const handleEventClick = (event) => {
        setSelectedPost(event);
        setModalOpen(true);
    };

    /**
     * Handles post editing
     * @param {Object} post - The post to edit
     */
    const handleEditPost = (post) => {
        setModalOpen(false);
        // Additional edit logic can be added here
    };

    /**
     * Custom event component for calendar
     * @param {Object} event - The event to render
     */
    const CustomEvent = ({event}) => (<CustomEventCard>
        <Typography variant="body2" sx={{flexGrow: 1}}>
            {event.platform_details[0]?.platform_specific_text || event.post_text}
            <RenderSocialIcons post={event}/>
        </Typography>
    </CustomEventCard>);

    /**
     * Handles template search
     * @param {Event} event - The input change event
     */
    const handleTemplateSearchChange = (event) => {
        setTemplateSearchTerm(event.target.value);
    };

    /**
     * Handles template type filter change
     * @param {Event} event - The select change event
     */
    const handleTemplateTypeFilterChange = (event) => {
        setTemplateTypeFilter(event.target.value);
    };

    /**
     * Handles template view mode change
     * @param {string} mode - The new view mode ('grid' or 'list')
     */
    const handleTemplateViewModeChange = (mode) => {
        setTemplateViewMode(mode);
    };

    /**
     * Filters templates based on search term and type filter
     * @returns {Array} Filtered templates array
     */
    const getFilteredTemplates = () => {
        return templates.filter(template => {
            // Search filter
            const matchesSearch = !templateSearchTerm || template.name.toLowerCase().includes(templateSearchTerm.toLowerCase());

            // Type filter
            const matchesType = templateTypeFilter === 'all' || (templateTypeFilter === 'video' && template.is_video) || (templateTypeFilter === 'image' && !template.is_video);

            return matchesSearch && matchesType;
        });
    };

    /**
     * Renders the upgrade message for inactive accounts
     */
    const renderUpgradeMessage = () => (<UpgradeWrapper elevation={3}>
        <Typography variant="h4" component="h2" gutterBottom>
            {isAccountExpired ? 'Your Subscription Has Expired' : 'Account Inactive'}
        </Typography>
        <Typography variant="body1" gutterBottom>
            {isAccountExpired ? 'Your subscription has expired.' : 'Your account is currently inactive.'}
        </Typography>
        <Typography variant="body1" gutterBottom>
            Upgrade your account to continue using Solo Social's features and manage your social media presence
            effectively.
        </Typography>
        <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/profile"
            size="large"
            sx={{mt: 2}}
        >
            Upgrade Now
        </Button>
    </UpgradeWrapper>);

    /**
     * Renders the main action buttons for creating posts and templates
     */
    const renderActionButtons = () => (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Hero Section - Row 0 */}
            <Grid item xs={12}>
                <Typography 
                    variant="h4" 
                    component="h2" 
                    gutterBottom
                    sx={{
                        pt: 6, // 48px top padding
                        pb: 3, // 24px bottom padding
                        fontSize: '2rem', // 32px
                        lineHeight: '2.5rem', // 40px
                        fontWeight: 600,
                        textAlign: 'center'
                    }}
                >
                    Start Creating Content
                </Typography>
                <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{
                        fontSize: '0.875rem', // 14px
                        lineHeight: '1.375rem', // 22px
                        maxWidth: '60ch',
                        mx: 'auto',
                        textAlign: 'center',
                        mb: 4
                    }}
                >
                    Create engaging content for your social media platforms
                </Typography>
            </Grid>

            {/* Dual CTA Cards - Row 1 - Fixed alignment */}
            {canCreatePosts && (
                <Grid item xs={12} sm={6}>
                    <Card
                        sx={{
                            height: '220px', // Capped height
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: canCreatePosts ? 'divider' : 'grey.200', // Gray border for inactive
                            background: canCreatePosts 
                                ? 'rgba(0, 1, 32, 0.02)' // 2% tint of brand navy for active
                                : 'rgba(128, 128, 128, 0.01)', // 1% gray tint for inactive
                            transition: 'all 0.3s ease',
                            cursor: canCreatePosts ? 'pointer' : 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            opacity: canCreatePosts ? 1 : 0.7, // Slight opacity for inactive
                            '&:hover': canCreatePosts ? {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                borderColor: 'primary.main'
                            } : {}
                        }}
                        component={canCreatePosts ? Link : 'div'}
                        to={canCreatePosts ? "/post/create" : undefined}
                        style={{ textDecoration: 'none' }}
                    >
                        <CardContent sx={{ 
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}>
                            {/* Icon positioned top-left */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                <PostIcon sx={{ 
                                    fontSize: 40, 
                                    color: canCreatePosts ? 'primary.main' : 'grey.400',
                                    mr: 2
                                }} />
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    color={canCreatePosts ? "primary" : "text.secondary"}
                                    sx={{
                                        fontSize: '1.5rem', // 24px
                                        lineHeight: '2rem', // 32px
                                        fontWeight: 600
                                    }}
                                >
                                    Create Post
                                </Typography>
                            </Box>
                            
                            {/* Content area */}
                            <Box sx={{ flex: 1, mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Schedule and publish content across all your social media platforms
                                </Typography>
                            </Box>
                            
                            {/* Button flush bottom-left */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    variant="contained"
                                    sx={{ 
                                        py: 2, // 16px vertical padding
                                        minWidth: 120
                                    }}
                                    disabled={!canCreatePosts}
                                >
                                    New Post
                                </Button>
                            </Box>
                            
                            {!canCreatePosts && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    Upgrade to create posts
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
            
            {canAccessTemplateEditor && (
                <Grid item xs={12} sm={6}>
                    <Card
                        sx={{
                            height: '220px', // Capped height
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: canAccessTemplateEditor ? 'divider' : 'grey.200', // Gray border for inactive
                            background: canAccessTemplateEditor 
                                ? 'rgba(253, 199, 39, 0.02)' // 2% tint of brand yellow for active
                                : 'rgba(128, 128, 128, 0.01)', // 1% gray tint for inactive
                            transition: 'all 0.3s ease',
                            cursor: canAccessTemplateEditor ? 'pointer' : 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            opacity: canAccessTemplateEditor ? 1 : 0.7, // Slight opacity for inactive
                            '&:hover': canAccessTemplateEditor ? {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                borderColor: 'secondary.main'
                            } : {}
                        }}
                        component={canAccessTemplateEditor ? Link : 'div'}
                        to={canAccessTemplateEditor ? "/editor" : undefined}
                        style={{ textDecoration: 'none' }}
                    >
                        <CardContent sx={{ 
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}>
                            {/* Icon positioned top-left */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                <CategoryIcon sx={{ 
                                    fontSize: 40, 
                                    color: canAccessTemplateEditor ? 'secondary.main' : 'grey.400',
                                    mr: 2
                                }} />
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    color={canAccessTemplateEditor ? "secondary" : "text.secondary"}
                                    sx={{
                                        fontSize: '1.5rem', // 24px
                                        lineHeight: '2rem', // 32px
                                        fontWeight: 600
                                    }}
                                >
                                    Design Template
                                </Typography>
                            </Box>
                            
                            {/* Content area */}
                            <Box sx={{ flex: 1, mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Design reusable templates for consistent branding across platforms
                                </Typography>
                            </Box>
                            
                            {/* Button flush bottom-left */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    sx={{ 
                                        py: 2, // 16px vertical padding
                                        minWidth: 120
                                    }}
                                    disabled={!canAccessTemplateEditor}
                                >
                                    New Template
                                </Button>
                            </Box>
                            
                            {!canAccessTemplateEditor && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    Upgrade to create templates
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    );

    /**
     * Renders the scheduler configuration section with Linked Accounts and User Statistics
     */
    const renderSchedulerConfig = () => {
        // Use optimized data with fallbacks to legacy auth data
        const socialAccounts = profile.data?.social_accounts || user_details?.userprofile?.social_accounts || [];
        const statsData = stats.data || user_details?.stats || {};

        // Group accounts by platform
        const groupedAccounts = groupAccountsByPlatform(socialAccounts);

        // Start with all platforms collapsed by default

        return (
            <Grid container spacing={3} sx={{mb: 4}}>
                {/* Linked Accounts - Row 3, Col 1 (4 col span) */}
                {canAccessSocialConnections && (
                    <Grid item xs={12} md={5}>
                        <SchedulerCard sx={{ 
                            minHeight: '200px',
                            height: '100%', // Match the height of the Monthly Activity card
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                <Typography variant="h6" sx={{flexGrow: 1}}>
                                    Linked Accounts
                                </Typography>
                                <LinkIcon sx={{color: 'primary.main', mr: 1}}/>
                                <Chip
                                    label={`${socialAccounts.length} Connected`}
                                    size="small"
                                    color={socialAccounts.length > 0 ? 'success' : 'default'}
                                />
                            </Box>

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {socialAccounts.length === 0 ? (
                                    <Box sx={{textAlign: 'center', py: 3}}>
                                        <Typography variant="h6" gutterBottom>
                                            Ready to get started?
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Connect your social media accounts to start posting
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                            Choose from Twitter, Instagram, LinkedIn, TikTok, and more
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            component={Link}
                                            to="/profile"
                                            sx={{ mt: 1 }}
                                        >
                                            Connect Accounts
                                        </Button>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Platform Groups */}
                                        <Box sx={{ 
                                            flex: 1,
                                            overflowY: 'auto',
                                            mb: 2
                                        }}>
                                            {Object.entries(groupedAccounts).map(([platform, accounts]) => (
                                                <Box key={platform} sx={{ mb: 1 }}>
                                                    {/* Platform Header */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            py: 1,
                                                            px: 1.5,
                                                            borderRadius: 1,
                                                            backgroundColor: 'action.hover',
                                                            '&:hover': {
                                                                backgroundColor: 'action.selected',
                                                            }
                                                        }}
                                                        onClick={() => togglePlatform(platform)}
                                                    >
                                                        {getPlatformIcon(platform)}
                                                        <Typography 
                                                            variant="body2" 
                                                            fontWeight="medium" 
                                                            sx={{ ml: 1, flex: 1 }}
                                                        >
                                                            {getPlatformDisplayName(platform)}
                                                        </Typography>
                                                        <Chip
                                                            label={accounts.length}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ mr: 1 }}
                                                        />
                                                        {expandedPlatforms.has(platform) ? (
                                                            <ExpandLessIcon fontSize="small" />
                                                        ) : (
                                                            <ExpandMoreIcon fontSize="small" />
                                                        )}
                                                    </Box>

                                                    {/* Platform Accounts */}
                                                    <Collapse in={expandedPlatforms.has(platform)}>
                                                        <Box sx={{ ml: 3, mt: 1 }}>
                                                            {accounts.map((account, index) => (
                                                                <Chip
                                                                    key={`${account.platform}-${index}`}
                                                                    avatar={
                                                                        <Avatar 
                                                                            src={account.profile_image_url || account.avatar_url}
                                                                            sx={{ width: 20, height: 20 }}
                                                                        >
                                                                            {(account.username || account.name || account.platform)?.charAt(0)?.toUpperCase()}
                                                                        </Avatar>
                                                                    }
                                                                    label={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {account.username || account.handle || 'Unknown'}
                                                                            </Typography>
                                                                            {/* Success green dot */}
                                                                            <Box
                                                                                sx={{
                                                                                    width: 6,
                                                                                    height: 6,
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: account.is_active ? 'success.main' : 'grey.400',
                                                                                    ml: 0.5
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    }
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        mr: 0.5,
                                                                        maxWidth: '100%',
                                                                        '& .MuiChip-label': {
                                                                            maxWidth: '150px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Collapse>
                                                </Box>
                                            ))}
                                        </Box>
                                        
                                        {/* Improved call-to-action section */}
                                        <Box sx={{ 
                                            mt: 'auto', // Push to bottom
                                            pt: 2,
                                            borderTop: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            {/* Better information hierarchy */}
                                            <Box sx={{ mb: 1.5 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.primary"
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        lineHeight: 1.3,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    Expand Your Reach
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.4,
                                                        display: 'block'
                                                    }}
                                                >
                                                    Connect more platforms to maximize your social media presence
                                                </Typography>
                                            </Box>
                                            
                                            {/* Prominent CTA button */}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component={Link}
                                                to="/profile"
                                                fullWidth
                                                sx={{ 
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500,
                                                    py: 1,
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.main',
                                                        color: 'white'
                                                    }
                                                }}
                                            >
                                                Add Account
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </SchedulerCard>
                    </Grid>
                )}

                {/* KPI Deck - Row 2 (8 col span or full width) - Aligned with main grid */}
                <Grid item xs={12} md={canAccessSocialConnections ? 7 : 12}>
                    <SchedulerCard sx={{ 
                        minHeight: '200px', // Increased minHeight to match Linked Accounts and accommodate content
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                            <Typography variant="h6" sx={{flexGrow: 1}}>
                                Monthly Activity
                            </Typography>
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5}}>
                                <Chip
                                    label={statsData.month_year || 'Current Month'}
                                    size="small"
                                    variant="outlined"
                                />
                                {stats.loading && (<CircularProgress size={16}/>)}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1 }}> {/* Removed overflow hidden to allow natural content flow */}
                            {stats.error ? (
                                <Box sx={{textAlign: 'center', py: 3}}>
                                    <Typography variant="body2" color="error" gutterBottom>
                                        Failed to load statistics
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={stats.retry}
                                        sx={{mt: 1}}
                                    >
                                        Retry
                                    </Button>
                                </Box>
                            ) : (
                                // Reduced spacing - use gap: 1.5 instead of 2 for tighter spacing
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: 1.5, // Reduced from 2 to 1.5 (12px instead of 16px)
                                    justifyContent: 'space-between', // Changed from flex-start to space-between
                                    alignItems: 'flex-start',
                                    // Ensure proper wrapping on smaller screens
                                    '@media (max-width: 900px)': {
                                        justifyContent: 'center',
                                        gap: 1.2 // Slightly reduced mobile gap too
                                    }
                                }}>
                                    {canAccessTemplateEditor && (
                                        <>
                                            <StatCard
                                                icon={<VideoIcon/>}
                                                title="Video Templates"
                                                value={statsData.video_templates_created || 0}
                                                limit={statsData.video_templates_limit}
                                                color="primary"
                                                loading={stats.loading}
                                            />
                                            <StatCard
                                                icon={<ImageIcon/>}
                                                title="Image Templates"
                                                value={statsData.image_templates_created || 0}
                                                limit={statsData.image_templates_limit}
                                                color="secondary"
                                                loading={stats.loading}
                                            />
                                            <StatCard
                                                icon={<ImageIcon/>}
                                                title="Images Generated"
                                                value={statsData.images_generated || 0}
                                                limit={statsData.images_generated_limit}
                                                color="success"
                                                loading={stats.loading}
                                            />
                                        </>
                                    )}
                                    {canCreatePosts && (
                                        <StatCard
                                            icon={<PostIcon/>}
                                            title="Posts Scheduled"
                                            value={statsData.posts_scheduled || 0}
                                            limit={statsData.posts_scheduled_limit}
                                            color="warning"
                                            loading={stats.loading}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </SchedulerCard>
                </Grid>
            </Grid>
        );
    };

    /**
     * Renders the weekly calendar view - using HomeWeekView with optimized data
     * Enhanced with mobile-responsive container to prevent overflow
     */
    const renderWeeklySchedule = () => (
        <Box sx={{
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden',
            '& .MuiPaper-root': {
                maxWidth: '100%', 
                overflow: 'hidden'
            }
        }}>
            <HomeWeekView
                posts={post}
                onPostClick={handleEventClick}
                loading={postsLoading}
                onNavigateToCalendar={handleNavigateToCalendar}
            />
        </Box>
    );

    /**
     * Gets the appropriate welcome message based on user state
     */
    const getWelcomeMessage = () => {
        if (!canAccessTemplateEditor && !canCreatePosts) {
            return (<Typography variant="h5">
                Your current plan doesn't include access to templates or posts.
                Please <Link to="/pricing">upgrade your plan</Link> to access these features.
            </Typography>);
        }

        if (templates.length <= 0 && canAccessTemplateEditor) {
            return (<Typography variant="h5">
                Create your first <Link to="/editor">template</Link> to get started with Solo Social
            </Typography>);
        }

        if (!is_social_accounts_set && canAccessSocialConnections) {
            return (<Typography variant="h5">
                Head over to your <Link to="/profile">profile</Link> to connect
                your social media platforms.
            </Typography>);
        }

        if (canCreatePosts) {
            return (<Typography variant="h5">
                Create your first <Link to="/post/create">post</Link> to see
                how Solo Social can save you time
            </Typography>);
        }

        return (<Typography variant="h5">
            Welcome to Solo Social! Contact support if you need help getting started.
        </Typography>);
    };

    /**
     * Renders the main content based on active view
     */
    const renderMainContent = () => {
        switch (activeView) {
            case 'home':
            default:
                return (<Box>
                    {renderActionButtons()}
                        {renderSchedulerConfig()}
                    {canCreatePosts && <>
                        {renderWeeklySchedule()}
                    </>}
                </Box>);
            case 'calendar':
                return (<Box>
                    {canCreatePosts ? (<Box>
                        <ScheduleView
                            posts={post}
                            onPostClick={handleEventClick}
                            onFetchPosts={handleNavigate}
                            loading={postsLoading}
                        />
                    </Box>) : (<MessageWrapper>
                        {getWelcomeMessage()}
                    </MessageWrapper>)}
                </Box>);
            case 'templates':
                return (<Box>
                    {templates.length > 0 ? (<Box>
                        {/* Page Header */}
                        <PageHeader>
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}, fontWeight: 700
                                }}
                            >
                                Social Templates
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{fontSize: {xs: '0.9rem', sm: '1rem'}}}
                            >
                                Create and manage your social media templates for consistent branding.
                                {templates.length > 0 && (<> You
                                    have {templates.length} template{templates.length !== 1 ? 's' : ''} available.</>)}
                            </Typography>
                        </PageHeader>

                        {/* Search and Filter Controls */}
                        <ControlsContainer>
                            {/* Search */}
                            <SearchContainer>
                                <TextField
                                    fullWidth
                                    placeholder="Search templates..."
                                    value={templateSearchTerm}
                                    onChange={handleTemplateSearchChange}
                                    size={isMobile ? "medium" : "small"}
                                    InputProps={{
                                        startAdornment: (<InputAdornment position="start">
                                            <SearchIcon color="action"/>
                                        </InputAdornment>),
                                    }}
                                />
                            </SearchContainer>

                            {/* Template Type Filter */}
                            <FormControl size={isMobile ? "medium" : "small"} sx={{minWidth: 140}}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={templateTypeFilter}
                                    onChange={handleTemplateTypeFilterChange}
                                    label="Type"
                                >
                                    <MenuItem value="all">All Templates</MenuItem>
                                    <MenuItem value="image">
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <ImageIcon fontSize="small"/>
                                            Image Templates
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="video">
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <VideoIcon fontSize="small"/>
                                            Video Templates
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {/* View Toggle */}
                            {!isMobile && (<ViewToggleContainer>
                                <Button
                                    variant={templateViewMode === 'grid' ? 'contained' : 'outlined'}
                                    onClick={() => handleTemplateViewModeChange('grid')}
                                    startIcon={<GridIcon/>}
                                    size="small"
                                >
                                    Grid
                                </Button>
                                <Button
                                    variant={templateViewMode === 'list' ? 'contained' : 'outlined'}
                                    onClick={() => handleTemplateViewModeChange('list')}
                                    startIcon={<ListIcon/>}
                                    size="small"
                                >
                                    List
                                </Button>
                            </ViewToggleContainer>)}

                            {/* Create New Template Button */}
                            <Button
                                variant="contained"
                                component={Link}
                                to="/editor"
                                size={isMobile ? "medium" : "small"}
                                startIcon={<WebStoriesIcon/>}
                            >
                                New Template
                            </Button>
                        </ControlsContainer>

                        {/* Templates Display */}
                        {getFilteredTemplates().length === 0 ? (<Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                            textAlign="center"
                        >
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No templates match your filters
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try adjusting your search or filter criteria
                            </Typography>
                        </Box>) : (<>
                            {templateViewMode === 'grid' ? (<Grid container spacing={3}>
                                {getFilteredTemplates().map((template, index) => (
                                    <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                                        <TemplateCard template={template}/>
                                    </Grid>))}
                            </Grid>) : (<Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {getFilteredTemplates().map((template, index) => (<Paper
                                    key={index}
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        '&:hover': {
                                            backgroundColor: 'grey.50', cursor: 'pointer'
                                        }
                                    }}
                                >
                                    {/* Template Preview */}
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            backgroundColor: 'grey.200',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        {template.preview_img ? (<img
                                            src={template.preview_img}
                                            alt={template.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />) : (template.is_video ? (
                                            <VideoIcon color="action"/>) : (
                                            <ImageIcon color="action"/>))}
                                    </Box>

                                    {/* Template Info */}
                                    <Box sx={{flexGrow: 1, minWidth: 0}}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, mb: 0.5
                                        }}>
                                            <Typography variant="h6" component="h3" noWrap>
                                                {template.name}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={template.is_video ? 'Video' : 'Image'}
                                                color={template.is_video ? 'primary' : 'secondary'}
                                                icon={template.is_video ? <VideoIcon/> :
                                                    <ImageIcon/>}
                                            />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            Created {new Date(template.created_at || template.updated_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>

                                    {/* Template Actions */}
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Tooltip title="Edit Template">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                to={`/editor/${template.id}`}
                                                aria-label={`Edit ${template.name}`}
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Use Template">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                to={`/post/create?template=${template.id}`}
                                                aria-label={`Use ${template.name}`}
                                            >
                                                <PostIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Paper>))}
                            </Box>)}
                        </>)}
                    </Box>) : canAccessTemplateEditor ? (<MessageWrapper>
                        {getWelcomeMessage()}
                    </MessageWrapper>) : (<MessageWrapper>
                        <Typography variant="h5">
                            Template access is not available in your current plan.
                            <Link to="/pricing">Upgrade</Link> to access templates.
                        </Typography>
                    </MessageWrapper>)}
                </Box>);
        }
    };

    return (<ContentBucketsProvider>
        <DashboardContainer>
            <Box sx={{px: 3, py: 1}}>
                {/* Show upgrade message for inactive accounts */}
                {!isAccountActive && isAccountExpired && renderUpgradeMessage()}

                {/* Main content for active accounts */}
                {isAccountActive && !isAccountExpired && (<Box>
                    {renderMainContent()}
                </Box>)}
            </Box>
        </DashboardContainer>
    </ContentBucketsProvider>);
}

export default DashboardPage;


